import React, { useEffect, useMemo, useState } from 'react';
import { Text, Button, Group, Badge, Paper, SegmentedControl } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useGetPerformanceReviewRoundsRoundIdParticipants,
  useGetPerformanceReviewRoundsRoundIdAssignments,
  usePutPerformanceReviewRoundsRoundIdAssignments,
  getGetPerformanceReviewRoundsRoundIdAssignmentsSuggestionsUserIdQueryOptions,
} from '../../api/generated/performance-review/performance-review';
import { useUsersDirectory } from '../../hooks/useUserApi';
import { User } from '../../types';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { AssignmentsTableView } from './AssignmentsTableView';
import { AssignmentsMatrixView } from './AssignmentsMatrixView';
import { PopulatedParticipant, getParticipantUserId } from './assignmentsMatrix.helpers';

interface Assignment {
  subject?: string;
  respondent?: string;
}

interface Props {
  roundId: string;
}

const EMPTY_PARTICIPANTS: PopulatedParticipant[] = [];
const EMPTY_ASSIGNMENTS: Assignment[] = [];
const EMPTY_USERS: User[] = [];

export const PerformanceReviewAssignmentsEditor: React.FC<Props> = ({ roundId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pairs, setPairs] = useState<Record<string, string[]>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [view, setView] = useState<'table' | 'matrix'>('table');
  // Pause background refetch while the admin has unsaved edits — otherwise
  // the 10s poll re-hydrates `pairs` and wipes in-progress changes.
  const liveQuery = { refetchInterval: isDirty ? (false as const) : 10_000, staleTime: 0 };
  const { data: participants } = useGetPerformanceReviewRoundsRoundIdParticipants(roundId, {
    query: liveQuery,
  });
  const { data: existing, refetch } = useGetPerformanceReviewRoundsRoundIdAssignments(roundId, {
    query: liveQuery,
  });
  const setAll = usePutPerformanceReviewRoundsRoundIdAssignments();
  const { data: users } = useUsersDirectory();

  const participantsArr = useMemo(
    () => (participants as unknown as PopulatedParticipant[] | undefined) ?? EMPTY_PARTICIPANTS,
    [participants],
  );
  const existingArr = useMemo(
    () => (existing as unknown as Assignment[] | undefined) ?? EMPTY_ASSIGNMENTS,
    [existing],
  );
  const usersArr = useMemo(() => (users as User[] | undefined) ?? EMPTY_USERS, [users]);

  useEffect(() => {
    if (isDirty) return;
    const init: Record<string, string[]> = {};
    for (const p of participantsArr || []) {
      init[getParticipantUserId(p)] = [];
    }
    for (const e of existingArr || []) {
      const subj = String(e.subject);
      const resp = String(e.respondent);
      if (!init[subj]) init[subj] = [];
      if (init[subj].indexOf(resp) === -1) init[subj].push(resp);
    }
    setPairs(init);
  }, [participantsArr, existingArr, isDirty]);

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of usersArr) map[u._id] = u.name;
    return map;
  }, [usersArr]);

  const usersById = useMemo(() => {
    const map: Record<string, User> = {};
    for (const u of usersArr) map[u._id] = u;
    return map;
  }, [usersArr]);

  const participantIds = useMemo(
    () => participantsArr.map(getParticipantUserId).filter(Boolean),
    [participantsArr],
  );

  const requestedReviewersBySubject = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const p of participantsArr) map[getParticipantUserId(p)] = p.requestedReviewers || [];
    return map;
  }, [participantsArr]);

  const toggleCell = (subjectId: string, reviewerId: string) => {
    if ((pairs[subjectId] || []).indexOf(reviewerId) !== -1) removeReviewer(subjectId, reviewerId);
    else addReviewer(subjectId, reviewerId);
  };

  const addReviewer = (subjectId: string, reviewerId: string) => {
    setPairs((prev) => {
      const list = prev[subjectId] || [];
      if (list.indexOf(reviewerId) !== -1) return prev;
      return { ...prev, [subjectId]: [...list, reviewerId] };
    });
    setIsDirty(true);
  };

  const removeReviewer = (subjectId: string, reviewerId: string) => {
    setPairs((prev) => ({
      ...prev,
      [subjectId]: (prev[subjectId] || []).filter((x) => x !== reviewerId),
    }));
    setIsDirty(true);
  };

  const autoFill = async (subjectId: string) => {
    try {
      const ids = (await queryClient.fetchQuery(
        getGetPerformanceReviewRoundsRoundIdAssignmentsSuggestionsUserIdQueryOptions(
          roundId,
          subjectId,
        ),
      )) as unknown as string[];
      setPairs((prev) => {
        const cur = prev[subjectId] || [];
        const merged = cur.slice();
        for (const rid of ids) {
          if (rid !== subjectId && merged.indexOf(rid) === -1) merged.push(rid);
        }
        return { ...prev, [subjectId]: merged };
      });
      setIsDirty(true);
    } catch (e) {
      showToast({
        title: t('perfReview.autoFillFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  const handleSave = async () => {
    const flat: { respondent: string; subject: string }[] = [];
    for (const [subject, reviewers] of Object.entries(pairs)) {
      for (const r of reviewers) flat.push({ respondent: r, subject });
    }
    try {
      await setAll.mutateAsync({ roundId, data: { pairs: flat } });
      await refetch();
      setIsDirty(false);
      showToast({
        title: t('perfReview.assignmentsSaved'),
        message: t('perfReview.pairsSaved', { count: flat.length }),
        color: 'teal',
      });
    } catch (e) {
      showToast({
        title: t('perfReview.saveAssignmentsFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  const totalAssigned = Object.values(pairs).reduce((acc, list) => acc + list.length, 0);

  return (
    <Paper withBorder>
      <Group justify="space-between" p="md" wrap="wrap" align="flex-start">
        <div style={{ flex: 1, minWidth: 240 }}>
          <Text size="sm">{t('perfReview.assignmentsHelp')}</Text>
          <Text size="xs" c="dimmed">
            {t('perfReview.assignmentsHelpDetail')}
          </Text>
        </div>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => setView(v as 'table' | 'matrix')}
            data={[
              { label: t('perfReview.viewTable'), value: 'table' },
              { label: t('perfReview.viewMatrix'), value: 'matrix' },
            ]}
          />
          <Badge variant="light" color="blue">
            {t('perfReview.totalPairs', { count: totalAssigned })}
          </Badge>
          <Button onClick={handleSave} loading={setAll.isPending} disabled={!isDirty}>
            {t('perfReview.save')}
          </Button>
        </Group>
      </Group>
      {view === 'table' ? (
        <AssignmentsTableView
          participants={participantsArr}
          usersArr={usersArr}
          pairs={pairs}
          userNameById={userNameById}
          onAddReviewer={addReviewer}
          onRemoveReviewer={removeReviewer}
          onAutoFill={autoFill}
        />
      ) : (
        <AssignmentsMatrixView
          participantIds={participantIds}
          usersById={usersById}
          pairs={pairs}
          requestedReviewersBySubject={requestedReviewersBySubject}
          onToggle={toggleCell}
        />
      )}
    </Paper>
  );
};
